import { twMerge } from 'tailwind-merge';
import { CSSProperties, ReactNode } from 'react';
import { HTMLMotionProps, motion } from 'motion/react';

import ConditionalRenderer from '../ConditionalRenderer';
import ConditionalWrapper from '../ConditionalWrapper';
import Skeleton from '../Skeleton';
import { Tooltip } from './Tooltip';
import { ClassNameComponent } from '@/utils/ClassNameComponent';
import { dropdown } from '@/utils/animations/commom';

export type TagColor =
  | 'primary'
  | 'secondary'
  | 'accent'
  | 'error'
  | 'warning'
  | 'confirm'
  | 'success'
  | 'info'
  | 'custom';

export type TagProps = Omit<HTMLMotionProps<'div'>, 'className' | 'children'> & {
  children?: ReactNode;
  className?: ClassNameComponent;
  testId?: string;
  text?: string;
  icon?: ReactNode;
  color?: TagColor;
  hideIconMobile?: boolean;
  style?: CSSProperties;
  loading?: boolean;
  tooltip?: string;
};

const colors: Record<TagColor, string> = {
  primary: 'text-primary bg-primary-content',
  secondary: 'text-secondary bg-secondary-content',
  accent: 'text-accent bg-accent-content border',
  warning: 'text-warning bg-warning-content border',
  confirm: 'text-base-100 bg-accent border',
  success: 'text-success bg-success-content border',
  info: 'text-info bg-info-content border',
  error: 'text-error bg-error-content border',
  custom: '',
};

export default function Tag(props: TagProps) {
  const {
    className,
    testId = 'tag',
    text,
    icon,
    hideIconMobile,
    style,
    tooltip,
    loading,
    children,
    color = 'primary',
    ...rest
  } = props;

  if (loading)
    return (
      <Skeleton className="px-1.5 py-0.5 w-40 bg-primary-content rounded-full" />
    );

  return (
    <ConditionalWrapper
      condition={!!tooltip}
      wrapper={<Tooltip text={tooltip} />}
    >
      <motion.span
        {...rest}
        {...dropdown}
        data-testid={testId}
        className={twMerge(
          'w-fit flex content-center items-center align-middle rounded-full px-3.5 py-1.5 text-[1rem] gap-4 font-500',
          className?.base,
          colors[color],
        )}
        style={style}
      >
        <ConditionalRenderer condition={icon}>
          <figure
            className={twMerge(
              'flex items-center justify-center',
              hideIconMobile ? 'hidden lg:flex' : 'flex',
              className?.figure,
            )}
          >
            {icon}
          </figure>
        </ConditionalRenderer>

        <ConditionalRenderer condition={text}>
          <p
            className={twMerge(
              'align-middle text-center leading-none pt-0.5',
              className?.text,
            )}
          >
            {text}
          </p>
        </ConditionalRenderer>

        {children}
      </motion.span>
    </ConditionalWrapper>
  );
}
